import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import dayjs from "dayjs";
import { AlertCircle, Calendar, X } from "lucide-react-native";
import { Controller, FieldValues } from "react-hook-form";
import { Button, ButtonIcon, ButtonText } from "../../ui/button";
import { Pressable } from "../../ui/pressable";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputProps } from "../types";
import { showDatePicker } from "./show";

type FormInputDateProp<TFieldValues extends FieldValues> = FormInputProps<
  TFieldValues,
  Date | null | undefined
> & {
  inputClassName?: string;
  dateTemplate?: string;
  showDeleteButton?: boolean;
  showCalendarIcon?: boolean;
};

export default function FormInputDate<TFieldValues extends FieldValues>(
  props: FormInputDateProp<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    labelSize = "md",
    placeholder,
    required,
    disabled,
    className,
    inputClassName = "px-2",
    size,
    dateTemplate = "DD [de] MMMM [de] YYYY",
    showDeleteButton = true,
    showCalendarIcon = true,
  } = props;
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, value, ref, onBlur },
        fieldState: { error },
      }) => {
        return (
          <>
            <FormControl
              isInvalid={!!error}
              isRequired={required}
              isDisabled={disabled}
              className={className}
              size={size}
            >
              {label && (
                <FormControlLabel>
                  <FormControlLabelText size={labelSize}>
                    {label}
                  </FormControlLabelText>
                </FormControlLabel>
              )}
              <Button
                isDisabled={disabled}
                variant="outline"
                className={`px-2 border-outline-300 flex-row overflow-hidden content-center data-[hover=true]:border-outline-400 data-[focus=true]:border-primary-700 data-[focus=true]:hover:border-primary-700 data-[disabled=true]:opacity-40 data-[disabled=true]:hover:border-background-300 items-center h-12 ${inputClassName}`}
                rounded="2xl"
                size={size}
                onPress={() =>
                  showDatePicker({
                    date: value,
                    onConfirm: onChange,
                    onBlur,
                  })
                }
              >
                {showCalendarIcon && (
                  <ButtonIcon as={Calendar} className="text-typography-700" />
                )}
                <ButtonText
                  className={`flex-1 shrink ${value ? "" : "text-typography-500"}`}
                  size={size}
                  ref={focusScrollRef(ref)}
                >
                  {(value && dayjs(value).format(dateTemplate)) || placeholder}
                </ButtonText>
                {!!value && showDeleteButton && (
                  <Pressable onPress={() => onChange(null)}>
                    <ButtonIcon as={X} />
                  </Pressable>
                )}
              </Button>
              <FormControlError>
                <FormControlErrorIcon as={AlertCircle} className="mr-1" />
                <FormControlErrorText>{error?.message}</FormControlErrorText>
              </FormControlError>
            </FormControl>
          </>
        );
      }}
    />
  );
}
